import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { api } from "@/lib/api";
import { useCurrency } from "@/lib/CurrencyProvider";
import { CalendarCheck, Stethoscope, ShoppingBag, MessageCircle, ArrowRight, Phone, ShieldCheck, MapPin, Instagram, Music2, Award } from "lucide-react";

export default function Home() {
  const [services, setServices] = useState([]);
  const [settings, setSettings] = useState({});
  const { format } = useCurrency();

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const phone = settings.phone || "+234 813 977 1270";
  const wa = (settings.whatsapp || phone).replace(/\D/g, "");
  const waLink = `https://wa.me/${wa}?text=${encodeURIComponent("Hello Dr. Juwon, I'd like to consult about my pet.")}`;
  const drPhoto = settings.owner_photo_url || "/dr-juwon.jpg";

  return (
    <div className="min-h-screen ankara" data-testid="home-page">
      <Header />

      {/* Emergency strip */}
      <div className="bg-[#0F766E] text-white text-xs md:text-sm" data-testid="emergency-strip">
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-center gap-2 flex-wrap">
          <Phone size={14} />
          <span>Emergency? Seek immediate veterinary attention. Online consultations may not receive an immediate response.</span>
        </div>
      </div>

      {/* HERO — with real Dr. Juwon */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 md:pt-16 pb-10 md:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 z-10 order-2 lg:order-1">
            <div className="flex flex-wrap items-center gap-2 mb-6">
              <span className="chip chip-emerald" data-testid="chip-accepting">Now accepting patients</span>
              <span className="chip chip-gold">Dr. Juwon · DVM</span>
              <span className="chip chip-terra">🇳🇬 Ibadan, Nigeria</span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] text-[#1C1917]" data-testid="hero-title">
              Professional care.<br />
              <span className="text-[#0F766E] italic">Compassionate</span> service.<br />
              <span className="text-[#C2410C] italic">Healthier</span> pets.
            </h1>
            <p className="mt-6 text-base md:text-lg text-[#57534E] max-w-xl leading-relaxed">
              <b>Jay's Veterinary Consult and Services</b>, led by <b>Dr. Juwon (DVM, University of Ibadan)</b> — reliable, evidence-based care for dogs, cats, livestock and more. Book, consult online, or shop with global currencies.
            </p>
            <div className="mt-8 grid grid-cols-2 sm:flex sm:flex-wrap gap-3">
              <Link to="/book" className="btn-primary inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full text-sm font-semibold" data-testid="hero-book-btn">
                <CalendarCheck size={16} /> Book an Appointment
              </Link>
              <Link to="/consult" className="btn-terra inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full text-sm font-semibold" data-testid="hero-consult-btn">
                <Stethoscope size={16} /> Start a Consultation
              </Link>
              <Link to="/shop" className="btn-gold inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full text-sm font-semibold" data-testid="hero-shop-btn">
                <ShoppingBag size={16} /> Shop Pet Products
              </Link>
              <a href={waLink} target="_blank" rel="noopener noreferrer" className="btn-whatsapp inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full text-sm font-semibold" data-testid="hero-contact-btn">
                <MessageCircle size={16} /> Contact Dr. Juwon
              </a>
            </div>

            <div className="mt-10 flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#57534E]">
              <div className="flex items-center gap-2"><Award size={16} className="text-[#0F766E]" /> DVM · University of Ibadan</div>
              <div className="flex items-center gap-2"><Phone size={16} className="text-[#0F766E]" /> {phone}</div>
              <div className="flex items-center gap-2"><MapPin size={16} className="text-[#0F766E]" /> {settings.address}</div>
            </div>
          </div>

          <div className="lg:col-span-6 relative order-1 lg:order-2">
            <div className="relative rounded-[2rem] overflow-hidden aspect-[4/5] max-h-[720px] border-4 border-white shadow-[0_30px_80px_-30px_rgba(15,118,110,0.45)]" data-testid="hero-portrait">
              <img src={drPhoto} alt="Dr. Juwon — Lead Veterinarian" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1C1917]/50 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 bg-white/95 backdrop-blur rounded-2xl p-5 border border-[#EAD9B0]">
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 rounded-full bg-[#0F766E] text-white flex items-center justify-center font-display text-xl">J</div>
                  <div>
                    <div className="font-display font-semibold text-base">Dr. Juwon</div>
                    <div className="text-[11px] text-[#57534E]">DVM · University of Ibadan · Lead Veterinarian</div>
                  </div>
                </div>
                <p className="mt-3 text-sm text-[#1C1917] font-display italic">"Every pet deserves care that's honest, gentle, and rooted in science."</p>
              </div>
            </div>
            <div className="hidden md:block absolute -top-6 -left-6 w-24 h-24 rounded-full bg-[#D4A017]/30 blur-2xl" />
            <div className="hidden md:block absolute -bottom-8 -right-6 w-40 h-40 rounded-full bg-[#0F766E]/25 blur-3xl" />
          </div>
        </div>
      </section>

      {/* SERVICES SNAPSHOT */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24" data-testid="home-services">
        <div className="flex items-end justify-between gap-6 mb-10">
          <div>
            <div className="chip chip-emerald mb-3">Our services</div>
            <h2 className="font-display text-3xl md:text-4xl">Care for pets, livestock &amp; farms.</h2>
          </div>
          <Link to="/services" className="hidden md:inline-flex items-center gap-2 text-sm font-medium text-[#0F766E] hover:text-[#0B5A55]" data-testid="services-view-all">View all <ArrowRight size={16} /></Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {services.slice(0, 6).map((s) => (
            <Link key={s.service_id} to="/book" state={{ preselectService: s.service_id }} className="card-lift bg-white border border-[#EAD9B0] rounded-2xl p-6 block" data-testid={`svc-card-${s.service_id}`}>
              <div className="flex items-center justify-between">
                <div className="h-11 w-11 rounded-xl bg-[#0F766E]/10 flex items-center justify-center"><Stethoscope size={20} className="text-[#0F766E]" /></div>
                <span className="chip chip-gold">{s.category}</span>
              </div>
              <div className="mt-4 font-display text-xl">{s.name}</div>
              <p className="mt-2 text-sm text-[#57534E] leading-relaxed line-clamp-3">{s.description}</p>
              <div className="mt-4 flex items-center justify-between text-sm">
                <span className="text-[#57534E]">{s.duration_min} min</span>
                <span className="font-semibold text-[#0F766E]">{s.price_display || format(s.price)}</span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* MEET DR. JUWON — full width photo band */}
      <section className="bg-white border-y-4 border-[#0F766E]/10 py-16 md:py-24" data-testid="meet-dr-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <div className="relative rounded-[2rem] overflow-hidden aspect-[4/5] border-4 border-[#D4A017] shadow-xl">
              <img src={drPhoto} alt="Dr. Juwon" className="w-full h-full object-cover" data-testid="dr-photo" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="chip chip-terra mb-3">Meet the veterinarian</div>
            <h2 className="font-display text-3xl md:text-5xl text-[#1C1917] leading-[1.05]">
              <span className="italic text-[#0F766E]">Dr. Juwon</span> — <br />trusted care in Ibadan and beyond.
            </h2>
            <p className="mt-6 text-[#57534E] leading-relaxed">
              Dr. Juwon is the owner and lead veterinary professional at Jay's Veterinary Consult and Services.
              With professional training in veterinary medicine from the <b>University of Ibadan</b>, Dr. Juwon is committed to providing
              reliable, compassionate, and evidence-based veterinary care while helping pet owners make informed decisions about their animals' health and wellbeing.
            </p>
            <ul className="mt-6 space-y-2 text-sm">
              <li className="flex items-center gap-2"><ShieldCheck size={16} className="text-[#0F766E]" /> Doctor of Veterinary Medicine (DVM), University of Ibadan</li>
              <li className="flex items-center gap-2"><ShieldCheck size={16} className="text-[#0F766E]" /> Consultations · Vaccinations · Deworming · Breeding · Livestock · Grooming</li>
              <li className="flex items-center gap-2"><MapPin size={16} className="text-[#0F766E]" /> {settings.address}</li>
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/consult" className="btn-primary px-6 py-3.5 rounded-full text-sm font-semibold inline-flex items-center gap-2" data-testid="dr-consult-btn">
                <Stethoscope size={16} /> Book a Consultation
              </Link>
              <a href={waLink} target="_blank" rel="noopener noreferrer" className="btn-whatsapp px-6 py-3.5 rounded-full text-sm font-semibold inline-flex items-center gap-2" data-testid="dr-whatsapp-btn">
                <MessageCircle size={16} /> WhatsApp Dr. Juwon
              </a>
              {settings.social_instagram && <a href={settings.social_instagram} target="_blank" rel="noopener noreferrer" className="px-5 py-3.5 rounded-full text-sm font-semibold border border-[#EAD9B0] hover:border-[#0F766E] inline-flex items-center gap-2" data-testid="ig-link"><Instagram size={16} /> Jaypet_services</a>}
              {settings.social_tiktok && <a href={settings.social_tiktok} target="_blank" rel="noopener noreferrer" className="px-5 py-3.5 rounded-full text-sm font-semibold border border-[#EAD9B0] hover:border-[#0F766E] inline-flex items-center gap-2" data-testid="tt-link"><Music2 size={16} /> Jaypet007</a>}
            </div>
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="home-cta">
        <div className="relative overflow-hidden rounded-3xl p-10 md:p-16 bg-[#0F766E] text-white">
          <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[#D4A017]/40 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full bg-[#C2410C]/30 blur-3xl" />
          <div className="relative grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="font-display text-3xl md:text-4xl leading-tight">Your pet's next visit is one tap away.</h2>
              <p className="mt-4 text-white/85 max-w-lg">Book online, start a consultation, or shop essentials. Pay in your local currency.</p>
            </div>
            <div className="flex flex-wrap md:justify-end gap-3">
              <Link to="/book" className="btn-gold px-6 py-3 rounded-full text-sm font-semibold">Book appointment</Link>
              <a href={`tel:+${wa}`} className="bg-white text-[#0F766E] px-6 py-3 rounded-full text-sm font-semibold inline-flex items-center gap-2"><Phone size={16} /> Call {phone}</a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
